// src/components/ui/ScrollProgress/ScrollProgress.tsx
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, useScroll, useSpring } from 'framer-motion';
import { ArrowUp } from 'lucide-react';

const ProgressContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9998;
  pointer-events: none;
`;

const ProgressBar = styled(motion.div)`
  height: 4px;
  background: linear-gradient(90deg, 
    ${props => props.theme.colors.secondary} 0%, 
    ${props => props.theme.colors.accent} 50%,
    ${props => props.theme.colors.primary} 100%
  );
  transform-origin: 0%;
  box-shadow: 0 2px 10px rgba(212, 165, 116, 0.5);
`;

const ScrollToTop = styled(motion.button)<{ $show: boolean }>`
  position: fixed;
  bottom: 40px;
  right: 40px;
  z-index: 9997;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, 
    ${props => props.theme.colors.secondary} 0%, 
    ${props => props.theme.colors.accent} 100%
  );
  border: none;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 30px rgba(212, 165, 116, 0.4);
  pointer-events: ${props => props.$show ? 'auto' : 'none'};
  opacity: ${props => props.$show ? 1 : 0};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(212, 165, 116, 0.5);
  }

  &:active {
    transform: translateY(-2px);
  }

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    bottom: 20px;
    right: 20px;
    width: 50px;
    height: 50px;
  }
`;

const ProgressPercentage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 0.7rem;
  font-weight: 700;
  color: white;
  display: none;

  ${ScrollToTop}:hover & {
    display: block;
  }
`;

const ScrollProgress: React.FC = () => {
  const [showButton, setShowButton] = useState(false);
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  useEffect(() => {
    const unsubscribe = scrollYProgress.onChange((latest) => {
      setShowButton(latest > 0.2);
    });

    return () => unsubscribe();
  }, [scrollYProgress]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <>
      <ProgressContainer>
        <ProgressBar style={{ scaleX }} />
      </ProgressContainer>

      <ScrollToTop
        $show={showButton}
        onClick={scrollToTop}
        initial={{ scale: 0 }}
        animate={{ scale: showButton ? 1 : 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <ArrowUp size={24} />
      </ScrollToTop>
    </>
  );
};

export default ScrollProgress;